import React, { useState, useEffect } from 'react';
import API from '../api';
import { ClipboardList, Plus, ArrowLeft, Check, Download, Edit2, Save, ChevronRight, AlertCircle, Users, X, Trash2, Mail } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';
import InvioEmailModal from '../components/InvioEmailModal';

export default function SALPage({ cantiereId, cantiereTitolo, onBack }) {
  const confirm = useConfirm();
  const [sals, setSals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editRighe, setEditRighe] = useState([]);
  const [editNote, setEditNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // Multi-pagatore: clienti del cantiere + modal di scelta pagatore al "Nuovo SAL"
  const [clientiCantiere, setClientiCantiere] = useState([]);
  // Multi-preventivo: preventivi del cantiere
  const [preventiviCantiere, setPreventiviCantiere] = useState([]);
  const [showSceltaSAL, setShowSceltaSAL] = useState(false);
  // selezione interna del modal di scelta SAL
  const [scelta, setScelta] = useState({ pagatoreId: '', preventivoId: '' });
  const [showInvioEmail, setShowInvioEmail] = useState(false);

  const load = () => API.get(`/api/cantieri/${cantiereId}/sal`).then(r => setSals(r.data));
  useEffect(() => {
    load();
    API.get(`/api/cantieri/${cantiereId}/clienti`)
      .then(r => setClientiCantiere(r.data?.clienti || []))
      .catch(() => setClientiCantiere([]));
    API.get(`/api/cantieri/${cantiereId}/preventivi`)
      .then(r => setPreventiviCantiere(r.data?.preventivi || []))
      .catch(() => setPreventiviCantiere([]));
  }, [cantiereId]);

  const multiCliente = clientiCantiere.length >= 2;
  const multiPreventivo = preventiviCantiere.length >= 2;

  const onClickNuovoSAL = () => {
    // Mostra modal se ci sono scelte da fare (più pagatori o più preventivi)
    if (multiCliente || multiPreventivo) {
      setScelta({ pagatoreId: '', preventivoId: '' });
      setShowSceltaSAL(true);
    } else {
      createSAL();
    }
  };

  const createSAL = async (pagatoreId = null, preventivoId = null) => {
    setShowSceltaSAL(false);
    setCreating(true);
    try {
      const params = new URLSearchParams();
      if (pagatoreId) params.append('pagatore_id', pagatoreId);
      if (preventivoId) params.append('preventivo_id', preventivoId);
      const qs = params.toString();
      const url = qs ? `/api/cantieri/${cantiereId}/sal?${qs}` : `/api/cantieri/${cantiereId}/sal`;
      const { data } = await API.post(url);
      load();
      openSAL(data.id);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setCreating(false);
  };

  const openSAL = async (id) => {
    const { data } = await API.get(`/api/sal/${id}`);
    setSelected(data);
    setEditRighe(data.righe?.map(r => ({ ...r })) || []);
    setEditNote(data.note || '');
    setEditing(false);
  };

  const saveSAL = async () => {
    setLoading(true);
    try {
      const { data } = await API.put(`/api/sal/${selected.id}`, { righe: editRighe, note: editNote });
      setSelected(data);
      setEditRighe(data.righe?.map(r => ({ ...r })) || []);
      setEditing(false);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const approvaSAL = async () => {
    if (!await confirm({ title: 'Approva SAL', message: 'Vuoi approvare questo Stato Avanzamento Lavori?\n\nUna volta approvato, lo stato non potrà più essere modificato.', confirmLabel: 'Approva SAL', variant: 'success' })) return;
    setLoading(true);
    try {
      const { data } = await API.post(`/api/sal/${selected.id}/approva`);
      setSelected(data);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const deleteSALById = async (s) => {
    if (!await confirm({
      title: 'Cancella SAL',
      message: `Vuoi cancellare definitivamente il SAL ${s.codice_sal}?\n\n${s.stato === 'approvato' ? '⚠ Il SAL è APPROVATO. Verrà ricalcolata la % di avanzamento del cantiere.\n\n' : ''}Questa operazione NON è reversibile.`,
      confirmLabel: 'Cancella definitivamente',
      variant: 'danger',
    })) return;
    try {
      await API.delete(`/api/sal/${s.id}`);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteSAL = async () => {
    if (!selected) return;
    const isApproved = selected.stato === 'approvato';
    if (!await confirm({
      title: 'Cancella SAL',
      message: `Vuoi cancellare definitivamente il SAL ${selected.codice_sal}?\n\n${isApproved ? '⚠ Il SAL è APPROVATO. Verrà ricalcolata la % di avanzamento del cantiere.\n\n' : ''}Questa operazione NON è reversibile. Se ci sono fatture collegate non annullate, l\'operazione verrà bloccata.`,
      confirmLabel: 'Cancella definitivamente',
      variant: 'danger',
    })) return;
    setLoading(true);
    try {
      await API.delete(`/api/sal/${selected.id}`);
      setSelected(null);
      setEditing(false);
      load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const downloadPdf = async (id, codice) => {
    try {
      const response = await API.get(`/api/sal/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${codice || 'SAL'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch { alert('Errore download PDF'); }
  };

  const updateQtyPeriodo = (idx, value) => {
    setEditRighe(prev => {
      const righe = [...prev];
      const r = { ...righe[idx] };
      r.qty_periodo = parseFloat(value) || 0;
      r.qty_progressivo = r.qty_precedente + r.qty_periodo;
      r.percentuale = r.qty_contratto ? Math.round(r.qty_progressivo / r.qty_contratto * 1000) / 10 : 0;
      r.importo_periodo = Math.round(r.qty_periodo * r.prezzo_unitario * 100) / 100;
      r.importo_progressivo = Math.round(r.qty_progressivo * r.prezzo_unitario * 100) / 100;
      righe[idx] = r;
      return righe;
    });
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const totPeriodo = editRighe.reduce((s, r) => s + (r.importo_periodo || 0), 0);
  const totProgressivo = editRighe.reduce((s, r) => s + (r.importo_progressivo || 0), 0);
  const totContratto = editRighe.reduce((s, r) => s + (r.importo_contratto || 0), 0);
  const pctAvanz = totContratto ? Math.round(totProgressivo / totContratto * 1000) / 10 : 0;

  // ── SAL Detail ────────────────────────────────────────────────────
  if (selected) {
    const righe = editing ? editRighe : (selected.righe || []);
    return (
      <div className="animate-fade-in" data-testid="sal-detail">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft size={18} /></button>
            <div>
              <h2 className="text-xl sm:text-2xl font-extrabold">{selected.codice_sal}</h2>
              <p className="text-xs text-slate-500">SAL n.{selected.numero_sal} - {selected.data_sal ? new Date(selected.data_sal).toLocaleDateString('it-IT') : ''}</p>
            </div>
            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${selected.stato === 'approvato' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
              {selected.stato === 'approvato' ? 'Approvato' : 'Bozza'}
            </span>
            {selected.pagatore_label && (
              <span className="ml-1 px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 flex items-center gap-1">
                <Users size={10}/>Pagatore: {selected.pagatore_label}
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {selected.stato === 'bozza' && !editing && (
              <button onClick={() => setEditing(true)} className="px-3 py-1.5 border border-slate-200 text-sm font-bold rounded-md hover:bg-slate-50 flex items-center gap-1" data-testid="edit-sal-btn">
                <Edit2 size={14}/> Compila
              </button>
            )}
            {editing && (
              <button onClick={saveSAL} disabled={loading} className="px-3 py-1.5 bg-primary text-white text-sm font-bold rounded-md hover:bg-primary-hover flex items-center gap-1 disabled:opacity-50" data-testid="save-sal-btn">
                <Save size={14}/> {loading ? 'Salvataggio...' : 'Salva'}
              </button>
            )}
            {selected.stato === 'bozza' && !editing && (
              <button onClick={approvaSAL} disabled={loading} className="px-3 py-1.5 bg-accent-success text-white text-sm font-bold rounded-md hover:bg-green-700 flex items-center gap-1 disabled:opacity-50" data-testid="approva-sal-btn">
                <Check size={14}/> Approva
              </button>
            )}
            <button onClick={() => downloadPdf(selected.id, selected.codice_sal)} className="px-3 py-1.5 bg-accent-info text-white text-sm font-bold rounded-md hover:bg-blue-700 flex items-center gap-1" data-testid="pdf-sal-btn">
              <Download size={14}/> PDF
            </button>
            <button onClick={() => setShowInvioEmail(true)} className="px-3 py-1.5 bg-primary text-white text-sm font-bold rounded-md hover:bg-primary flex items-center gap-1" data-testid="invia-sal-email-btn">
              <Mail size={14}/> Invia
            </button>
            <button onClick={deleteSAL} disabled={loading} title="Cancella SAL"
              className="px-3 py-1.5 border border-red-200 text-red-600 text-sm font-bold rounded-md hover:bg-red-50 flex items-center gap-1 disabled:opacity-50" data-testid="delete-sal-btn">
              <Trash2 size={14}/> Cancella
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold">Avanzamento Lavori</span>
            <span className="text-lg font-black text-primary">{editing ? pctAvanz : selected.percentuale_avanzamento}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div className="bg-primary rounded-full h-3 transition-all duration-500" style={{ width: `${Math.min(100, editing ? pctAvanz : selected.percentuale_avanzamento)}%` }}></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-center">
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">Contratto</p><p className="font-mono font-bold text-sm">{fmt(totContratto || selected.totale_contratto)}</p></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">Precedente</p><p className="font-mono font-bold text-sm">{fmt(selected.totale_precedente)}</p></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">Periodo</p><p className="font-mono font-bold text-sm text-primary">{fmt(editing ? totPeriodo : selected.totale_periodo)}</p></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase">Progressivo</p><p className="font-mono font-bold text-sm text-accent-success">{fmt(editing ? totProgressivo : selected.totale_progressivo)}</p></div>
          </div>
        </div>

        {/* Note */}
        {editing ? (
          <textarea placeholder="Note SAL..." rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm mb-4" value={editNote} onChange={e => setEditNote(e.target.value)} />
        ) : selected.note ? (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-4 text-sm">{selected.note}</div>
        ) : null}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sal-table">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-2 py-2 text-left w-8">#</th>
                  <th className="px-2 py-2 text-left">Descrizione</th>
                  <th className="px-2 py-2 text-center w-16">U.M.</th>
                  {editing && (
                    <th className="px-2 py-2 text-right w-20 bg-slate-700" title="Q. contratto / Q. gia' eseguita in SAL precedenti">Contr. / Prec.</th>
                  )}
                  {editing && (
                    <th className="px-2 py-2 text-right w-24 bg-primary">Q. da aggiungere</th>
                  )}
                  <th className="px-2 py-2 text-right w-20">Q. eseguita</th>
                  <th className="px-2 py-2 text-right w-28">Importo</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${r.fuori_contabilita ? 'bg-red-50/40' : (r.percentuale >= 100 ? 'bg-green-50/40' : '')}`}>
                    <td className="px-2 py-1.5 text-slate-400">{r.riga_idx + 1}</td>
                    <td className="px-2 py-1.5 max-w-[420px]">
                      <p className="line-clamp-2">{r.descrizione}</p>
                      {r.fuori_contabilita && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded mt-0.5">
                          <AlertCircle size={9}/> Fuori contabilita' EdilCima
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center text-slate-500">{r.unita_misura}</td>
                    {editing && (
                      <td className="px-2 py-1.5 text-right text-[11px] text-slate-500 font-mono bg-slate-50 leading-tight">
                        <div>{r.qty_contratto}</div>
                        {(r.qty_precedente || 0) > 0 && <div className="text-[10px] text-slate-400">gia': {r.qty_precedente}</div>}
                      </td>
                    )}
                    {editing && (
                      <td className="px-2 py-1.5 text-right bg-primary">
                        <input type="number" step="0.01" className="w-20 px-1 py-0.5 border border-primary rounded text-sm text-right font-mono bg-white"
                          value={r.qty_periodo || ''} onChange={e => updateQtyPeriodo(i, e.target.value)} />
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{r.qty_progressivo || '-'}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{r.importo_progressivo ? fmt(r.importo_progressivo) : '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                <tr className="font-bold">
                  <td className="px-2 py-2" colSpan={editing ? 5 : 3}>TOTALE LAVORI ESEGUITI</td>
                  {editing && <td className="px-2 py-2 text-right font-mono text-primary">{fmt(editing ? totPeriodo : selected.totale_periodo)}</td>}
                  <td className="px-2 py-2 text-right"></td>
                  <td className="px-2 py-2 text-right font-mono text-base">{fmt(editing ? totProgressivo : selected.totale_progressivo)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ── SAL List ──────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" data-testid="sal-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {onBack && <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft size={18} /></button>}
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2">
              <ClipboardList size={24} className="text-primary" /> SAL
            </h2>
            <p className="text-xs text-slate-500">{cantiereTitolo}</p>
          </div>
        </div>
        <button onClick={onClickNuovoSAL} disabled={creating} data-testid="create-sal-btn"
          className="px-4 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50 text-sm">
          <Plus size={16} /> {creating ? 'Creazione...' : 'Nuovo SAL'}
        </button>
      </div>

      {/* Modal scelta pagatore/preventivo (multi-cliente o multi-preventivo) */}
      {showSceltaSAL && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="scelta-sal-modal">
          <div className="bg-white rounded-lg max-w-lg w-full shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2"><ClipboardList size={18}/>Configura nuovo SAL</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {multiCliente && multiPreventivo && 'Scegli pagatore e preventivo da includere nel SAL.'}
                  {multiCliente && !multiPreventivo && 'Scegli il pagatore destinatario del SAL.'}
                  {!multiCliente && multiPreventivo && 'Scegli quale preventivo includere nel SAL.'}
                </p>
              </div>
              <button onClick={() => setShowSceltaSAL(false)} className="text-slate-400 hover:text-slate-800"><X size={18}/></button>
            </div>
            <div className="p-4 space-y-4">
              {/* Selezione pagatore */}
              {multiCliente && (
                <div>
                  <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">1. Pagatore destinatario</p>
                  <div className="space-y-2">
                    <button onClick={() => setScelta(s => ({...s, pagatoreId: ''}))}
                      className={`w-full text-left p-3 rounded-md border-2 transition-colors ${scelta.pagatoreId === '' ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid="scelta-pagatore-all">
                      <span className="font-bold text-sm">Tutti i pagatori</span>
                      <p className="text-xs text-slate-500 mt-0.5">SAL completo (vecchio comportamento)</p>
                    </button>
                    {clientiCantiere.map((c, idx) => (
                      <button key={c.id} onClick={() => setScelta(s => ({...s, pagatoreId: c.id}))}
                        className={`w-full text-left p-3 rounded-md border-2 transition-colors ${scelta.pagatoreId === c.id ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`scelta-pagatore-${c.id}`}>
                        <div className="flex items-center gap-2">
                          {idx === 0 && <span className="text-[10px] font-bold text-primary bg-primary px-1.5 py-0.5 rounded">★ Principale</span>}
                          <span className="font-bold text-sm">{c.label}</span>
                        </div>
                        {(c.cf || c.piva) && <p className="text-xs text-slate-500 mt-0.5">{c.piva ? `P.IVA ${c.piva}` : `CF ${c.cf}`}</p>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Selezione preventivo */}
              {multiPreventivo && (
                <div>
                  <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">{multiCliente ? '2. ' : ''}Preventivo da includere</p>
                  <div className="space-y-2">
                    <button onClick={() => setScelta(s => ({...s, preventivoId: ''}))}
                      className={`w-full text-left p-3 rounded-md border-2 transition-colors ${scelta.preventivoId === '' ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid="scelta-preventivo-all">
                      <span className="font-bold text-sm">Tutti i preventivi ({preventiviCantiere.length})</span>
                      <p className="text-xs text-slate-500 mt-0.5">SAL aggregato su tutti i preventivi linkati</p>
                    </button>
                    {preventiviCantiere.map((p) => (
                      <button key={p.id} onClick={() => setScelta(s => ({...s, preventivoId: p.id}))}
                        className={`w-full text-left p-3 rounded-md border-2 transition-colors ${scelta.preventivoId === p.id ? 'border-primary bg-primary' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`scelta-preventivo-${p.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm">{p.numero_preventivo}</span>
                          {p.titolo && <span className="text-xs text-slate-600">— {p.titolo}</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">{fmt(p.totale_finale || 0)} totale</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex items-center justify-end gap-2">
              <button onClick={() => setShowSceltaSAL(false)} className="px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded">Annulla</button>
              <button onClick={() => createSAL(scelta.pagatoreId || null, scelta.preventivoId || null)} disabled={creating}
                className="px-4 py-1.5 bg-primary text-white text-sm font-bold rounded hover:bg-primary-hover disabled:opacity-50"
                data-testid="scelta-sal-conferma">
                {creating ? 'Creazione...' : 'Crea SAL'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {sals.map(s => (
          <div key={s.id} data-testid={`sal-${s.id}`}
            className="w-full bg-white border border-slate-200 rounded-md p-4 text-left hover:shadow-sm transition-all group flex items-center gap-3">
            <div onClick={() => openSAL(s.id)} className="flex-1 cursor-pointer">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold">{s.codice_sal}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${s.stato === 'approvato' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                      {s.stato === 'approvato' ? 'Approvato' : 'Bozza'}
                    </span>
                    {s.pagatore_label && (
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 flex items-center gap-1">
                        <Users size={10}/>{s.pagatore_label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{s.data_sal ? new Date(s.data_sal).toLocaleDateString('it-IT') : ''} - {s.righe?.length || 0} righe</p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Avanzamento</p>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-slate-200 rounded-full h-2">
                        <div className="bg-primary rounded-full h-2" style={{ width: `${Math.min(100, s.percentuale_avanzamento || 0)}%` }}></div>
                      </div>
                      <span className="font-mono font-bold text-sm">{s.percentuale_avanzamento || 0}%</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase font-bold">Progressivo</p>
                    <p className="font-mono font-bold">{fmt(s.totale_progressivo)}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-primary" />
                </div>
              </div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); deleteSALById(s); }} title="Cancella SAL"
              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-sal-list-${s.id}`}>
              <Trash2 size={14}/>
            </button>
          </div>
        ))}
        {sals.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <ClipboardList size={48} className="mx-auto mb-3 text-slate-300" />
            <p className="font-semibold">Nessun SAL ancora</p>
            <p className="text-sm mt-1">Crea il primo Stato Avanzamento Lavori</p>
          </div>
        )}
      </div>
      <InvioEmailModal
        open={showInvioEmail}
        onClose={() => setShowInvioEmail(false)}
        entity="sal"
        docId={selected?.id}
        docLabel="SAL"
        defaultOggetto={selected ? `SAL EDIL CIMA - ${selected.codice_sal || ''}` : ''}
      />
    </div>
  );
}
